import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  adminAuthAPI,
  adminProfileAPI,
  adminAnalyticsAPI,
  adminUsersAPI,
  adminMerchantsAPI,
  adminWalletAPI,
  adminHealthAPI,
  adminTokenStore,
} from '../../services/adminApi';

// Type definitions
export type MerchantStatus = 'pending' | 'approved' | 'rejected' | 'suspended';
export type UserStatus = 'active' | 'suspended' | 'banned' | 'pending_verification';

export interface AdminProfile {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin';
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  phoneNumber?: string;
  status: UserStatus;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Merchant {
  id: string;
  businessName: string;
  email: string;
  phoneNumber: string;
  address?: string;
  city?: string;
  country?: string;
  businessType?: string;
  taxId?: string;
  status: MerchantStatus;
  rejectionReason?: string;
  verificationDocuments?: any[];
  createdAt: string;
  updatedAt: string;
}

export interface SystemStatus {
  totalUsers: number;
  totalMerchants: number;
  totalRevenue: number;
  totalTransactions: number;
  activeUsers: number;
  pendingMerchants: number;
  systemHealth: string;
  lastUpdated: string;
}

export interface EarningsData {
  totalEarnings: number;
  currentMonth: number;
  previousMonth: number;
  currency: string;
  lastUpdated: string;
}

export interface AdminWallet {
  balance: number;
  currency: string;
  lastUpdated: string;
}

export interface TransactionVolumeItem {
  name: string;
  transactions: number;
  volume: number;
}

export interface UserGrowthItem {
  name: string;
  users: number;
}

export interface RecentTransaction {
  id: string;
  user: string;
  email: string;
  type: string;
  amount: number;
  fee: number;
  currency: string;
  status: string;
  description: string;
  date: string;
}

export interface EarningsAnalytics {
  revenueByMonth: { name: string; revenue: number; fees: number; count: number }[];
  totalRevenue: number;
  currentMonthRevenue: number;
  totalFees: number;
  activeWallets: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface AdminState {
  // Admin auth
  admin: AdminProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Dashboard stats
  dashboardStats: SystemStatus | null;
  statsLoading: boolean;

  // Users
  users: User[];
  usersTotal: number;
  usersLoading: boolean;
  usersError: string | null;
  selectedUser: User | null;

  // Merchants
  merchants: Merchant[];
  merchantsTotal: number;
  merchantsLoading: boolean;
  merchantsError: string | null;
  selectedMerchant: Merchant | null;

  // Earnings
  earnings: EarningsData | null;
  earningsLoading: boolean;

  // Wallet
  wallet: AdminWallet | null;
  walletLoading: boolean;

  // Health
  healthErrors: any[] | null;
  healthLoading: boolean;

  // Analytics
  transactionVolume: TransactionVolumeItem[];
  userGrowth: UserGrowthItem[];
  recentTransactions: RecentTransaction[];
  earningsAnalytics: EarningsAnalytics | null;
  analyticsLoading: boolean;
}

const initialState: AdminState = {
  admin: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  dashboardStats: null,
  statsLoading: false,

  users: [],
  usersTotal: 0,
  usersLoading: false,
  usersError: null,
  selectedUser: null,

  merchants: [],
  merchantsTotal: 0,
  merchantsLoading: false,
  merchantsError: null,
  selectedMerchant: null,

  earnings: null,
  earningsLoading: false,

  wallet: null,
  walletLoading: false,

  healthErrors: null,
  healthLoading: false,

  transactionVolume: [],
  userGrowth: [],
  recentTransactions: [],
  earningsAnalytics: null,
  analyticsLoading: false,
};

// Async Thunks
export const adminLogin = createAsyncThunk(
  'admin/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await adminAuthAPI.login(credentials);
      const { token, admin } = response.data.data || response.data;
      if (token) {
        adminTokenStore.setToken(token);
      }
      return { admin };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Admin login failed');
    }
  }
);

export const initializeSuperAdmin = createAsyncThunk(
  'admin/initialize',
  async (
    data: { email: string; password: string; name: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await adminAuthAPI.initializeSuperAdmin(data);
      const { token, admin } = response.data.data || response.data;
      if (token) {
        adminTokenStore.setToken(token);
      }
      return { admin };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Super admin initialization failed');
    }
  }
);

export const fetchAdminProfile = createAsyncThunk(
  'admin/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminProfileAPI.getProfile();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch admin profile');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'admin/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminProfileAPI.getDashboardStats();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (params: PaginationParams, { rejectWithValue }) => {
    try {
      const response = await adminUsersAPI.getAllUsers(params);
      const data = response.data.data || response.data;
      return {
        users: data.users || data,
        total: data.total || 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch users');
    }
  }
);

export const fetchUserById = createAsyncThunk(
  'admin/fetchUserById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await adminUsersAPI.getUserById(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch user');
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'admin/updateUserStatus',
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const response = await adminUsersAPI.updateUserStatus(id, status);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update user status');
    }
  }
);

export const fetchMerchants = createAsyncThunk(
  'admin/fetchMerchants',
  async (params: PaginationParams, { rejectWithValue }) => {
    try {
      const response = await adminMerchantsAPI.getMerchants(params);
      const data = response.data.data || response.data;
      return {
        merchants: data.merchants || data,
        total: data.total || 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch merchants');
    }
  }
);

export const fetchMerchantById = createAsyncThunk(
  'admin/fetchMerchantById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await adminMerchantsAPI.getMerchantById(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch merchant');
    }
  }
);

export const reviewMerchant = createAsyncThunk(
  'admin/reviewMerchant',
  async (
    { id, status, rejectionReason }: { id: string; status: string; rejectionReason?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await adminMerchantsAPI.reviewMerchant(id, status, rejectionReason);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to review merchant');
    }
  }
);

export const reviewMerchantDocument = createAsyncThunk(
  'admin/reviewMerchantDocument',
  async (
    { docId, status, rejectionReason }: { docId: string; status: string; rejectionReason?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await adminMerchantsAPI.reviewMerchantDocument(docId, status, rejectionReason);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to review document');
    }
  }
);

export const suspendMerchant = createAsyncThunk(
  'admin/suspendMerchant',
  async ({ id, reason }: { id: string; reason: string }, { rejectWithValue }) => {
    try {
      const response = await adminMerchantsAPI.suspendMerchant(id, reason);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to suspend merchant');
    }
  }
);

export const fetchAdminEarnings = createAsyncThunk(
  'admin/fetchEarnings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminWalletAPI.getAdminEarnings();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch earnings');
    }
  }
);

export const fetchAdminWallet = createAsyncThunk(
  'admin/fetchWallet',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminWalletAPI.getAdminWallet();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch wallet');
    }
  }
);

export const fetchHealthErrors = createAsyncThunk(
  'admin/fetchHealthErrors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminHealthAPI.getHealthErrors();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch health errors');
    }
  }
);

// Analytics Thunks
export const fetchTransactionVolume = createAsyncThunk(
  'admin/fetchTransactionVolume',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAnalyticsAPI.getTransactionVolume();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transaction volume');
    }
  }
);

export const fetchUserGrowth = createAsyncThunk(
  'admin/fetchUserGrowth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAnalyticsAPI.getUserGrowth();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user growth');
    }
  }
);

export const fetchRecentTransactions = createAsyncThunk(
  'admin/fetchRecentTransactions',
  async (limit: number = 5, { rejectWithValue }) => {
    try {
      const response = await adminAnalyticsAPI.getRecentTransactions(limit);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch recent transactions');
    }
  }
);

export const fetchEarningsAnalytics = createAsyncThunk(
  'admin/fetchEarningsAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAnalyticsAPI.getEarningsAnalytics();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch earnings analytics');
    }
  }
);

// Redux Slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminError: (state) => {
      state.error = null;
    },
    adminLogout: (state) => {
      state.admin = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      adminTokenStore.clear();
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    clearSelectedMerchant: (state) => {
      state.selectedMerchant = null;
    },
  },
  extraReducers: (builder) => {
    // Admin Login
    builder
      .addCase(adminLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.admin = action.payload.admin;
        state.token = null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Initialize Super Admin
    builder
      .addCase(initializeSuperAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeSuperAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.admin = action.payload.admin;
        state.token = null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(initializeSuperAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Admin Profile
    builder
      .addCase(fetchAdminProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAdminProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.admin = action.payload;
      })
      .addCase(fetchAdminProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Dashboard Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.users;
        state.usersTotal = action.payload.total;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload as string;
      });

    // Fetch User By ID
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload as string;
      });

    // Update User Status
    builder
      .addCase(updateUserStatus.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.usersLoading = false;
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload as string;
      });

    // Fetch Merchants
    builder
      .addCase(fetchMerchants.pending, (state) => {
        state.merchantsLoading = true;
        state.merchantsError = null;
      })
      .addCase(fetchMerchants.fulfilled, (state, action) => {
        state.merchantsLoading = false;
        state.merchants = action.payload.merchants;
        state.merchantsTotal = action.payload.total;
      })
      .addCase(fetchMerchants.rejected, (state, action) => {
        state.merchantsLoading = false;
        state.merchantsError = action.payload as string;
      });

    // Fetch Merchant By ID
    builder
      .addCase(fetchMerchantById.pending, (state) => {
        state.merchantsLoading = true;
      })
      .addCase(fetchMerchantById.fulfilled, (state, action) => {
        state.merchantsLoading = false;
        state.selectedMerchant = action.payload;
      })
      .addCase(fetchMerchantById.rejected, (state, action) => {
        state.merchantsLoading = false;
        state.merchantsError = action.payload as string;
      });

    // Review Merchant
    builder
      .addCase(reviewMerchant.pending, (state) => {
        state.merchantsLoading = true;
      })
      .addCase(reviewMerchant.fulfilled, (state, action) => {
        state.merchantsLoading = false;
        const index = state.merchants.findIndex((m) => m.id === action.payload.id);
        if (index !== -1) {
          state.merchants[index] = action.payload;
        }
        if (state.selectedMerchant?.id === action.payload.id) {
          state.selectedMerchant = action.payload;
        }
      })
      .addCase(reviewMerchant.rejected, (state, action) => {
        state.merchantsLoading = false;
        state.merchantsError = action.payload as string;
      });

    // Review Merchant Document
    builder
      .addCase(reviewMerchantDocument.pending, (state) => {
        state.merchantsLoading = true;
      })
      .addCase(reviewMerchantDocument.fulfilled, (state, action) => {
        state.merchantsLoading = false;
      })
      .addCase(reviewMerchantDocument.rejected, (state, action) => {
        state.merchantsLoading = false;
        state.merchantsError = action.payload as string;
      });

    // Suspend Merchant
    builder
      .addCase(suspendMerchant.pending, (state) => {
        state.merchantsLoading = true;
      })
      .addCase(suspendMerchant.fulfilled, (state, action) => {
        state.merchantsLoading = false;
        const index = state.merchants.findIndex((m) => m.id === action.payload.id);
        if (index !== -1) {
          state.merchants[index] = action.payload;
        }
        if (state.selectedMerchant?.id === action.payload.id) {
          state.selectedMerchant = action.payload;
        }
      })
      .addCase(suspendMerchant.rejected, (state, action) => {
        state.merchantsLoading = false;
        state.merchantsError = action.payload as string;
      });

    // Fetch Admin Earnings
    builder
      .addCase(fetchAdminEarnings.pending, (state) => {
        state.earningsLoading = true;
      })
      .addCase(fetchAdminEarnings.fulfilled, (state, action) => {
        state.earningsLoading = false;
        state.earnings = action.payload;
      })
      .addCase(fetchAdminEarnings.rejected, (state, action) => {
        state.earningsLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Admin Wallet
    builder
      .addCase(fetchAdminWallet.pending, (state) => {
        state.walletLoading = true;
      })
      .addCase(fetchAdminWallet.fulfilled, (state, action) => {
        state.walletLoading = false;
        state.wallet = action.payload;
      })
      .addCase(fetchAdminWallet.rejected, (state, action) => {
        state.walletLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Health Errors
    builder
      .addCase(fetchHealthErrors.pending, (state) => {
        state.healthLoading = true;
      })
      .addCase(fetchHealthErrors.fulfilled, (state, action) => {
        state.healthLoading = false;
        state.healthErrors = action.payload;
      })
      .addCase(fetchHealthErrors.rejected, (state, action) => {
        state.healthLoading = false;
        state.error = action.payload as string;
      });

    // Analytics
    builder
      .addCase(fetchTransactionVolume.pending, (state) => {
        state.analyticsLoading = true;
      })
      .addCase(fetchTransactionVolume.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.transactionVolume = action.payload;
      })
      .addCase(fetchTransactionVolume.rejected, (state) => {
        state.analyticsLoading = false;
      });

    builder
      .addCase(fetchUserGrowth.fulfilled, (state, action) => {
        state.userGrowth = action.payload;
      });

    builder
      .addCase(fetchRecentTransactions.fulfilled, (state, action) => {
        state.recentTransactions = action.payload;
      });

    builder
      .addCase(fetchEarningsAnalytics.fulfilled, (state, action) => {
        state.earningsAnalytics = action.payload;
      });
  },
});

export const { clearAdminError, adminLogout, clearSelectedUser, clearSelectedMerchant } = adminSlice.actions;
export default adminSlice.reducer;
